// client/src/utils/useAyPhaseReady.js
import { useEffect, useMemo, useState } from 'react';
import { getCurrentAcademicYearId, bootstrapAcademicYearFromServer } from './academicYearContext';

/**
 * useAyPhaseReady
 * Ensures SchoolId, Active Phase and Academic Year are available before pages fire data requests.
 * Emits a stable ready flag and returns the current values.
 */
export default function useAyPhaseReady() {
  const [ready, setReady] = useState(false);
  const [phase, setPhase] = useState(() => localStorage.getItem('schoolAdmin_activePhase') || '');
  const [schoolId, setSchoolId] = useState(() => localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '');
  const [ayId, setAyId] = useState(() => getNamespacedAyId());

  function getNamespacedAyId() {
    const sid = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId');
    const key = sid ? `${sid}:currentAcademicYearId` : 'currentAcademicYearId';
    return localStorage.getItem(key) || '';
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Ensure AY is bootstrapped
      try { await bootstrapAcademicYearFromServer(); } catch {}
      if (cancelled) return;

      const sid = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId') || '';
      const ph = localStorage.getItem('schoolAdmin_activePhase') || '';
      const ay = getNamespacedAyId();

      setSchoolId(sid);
      setPhase(ph);
      setAyId(ay);

      // Consider the app ready when school and AY are known; phase may be initialized later
      setReady(!!sid && !!ay);
    })();

    const onAy = () => setAyId(getNamespacedAyId());
    const onPhase = (e) => {
      const fromEvent = (e && e.detail && e.detail.code) || '';
      const fromStorage = localStorage.getItem('schoolAdmin_activePhase') || '';
      const next = fromEvent || fromStorage || '';
      setPhase(next);
    };

    window.addEventListener('app:ay:changed', onAy);
    window.addEventListener('app:phase:changed', onPhase);

    return () => {
      cancelled = true;
      window.removeEventListener('app:ay:changed', onAy);
      window.removeEventListener('app:phase:changed', onPhase);
    };
  }, []);

  return useMemo(() => ({ ready, activePhase: phase, ayId, schoolId }), [ready, phase, ayId, schoolId]);
}
